import { isAxiosError } from 'axios';

export type AppErrorKind =
  | 'auth'
  | 'permission'
  | 'notFound'
  | 'server'
  | 'network'
  | 'abort'
  | 'validation'
  | 'unknown';

export class ApiError extends Error {
  readonly kind: AppErrorKind;
  readonly status?: number;
  readonly method?: string;
  readonly path?: string;
  readonly requestId?: string;
  readonly safeDetails?: unknown;

  constructor(params: {
    message: string;
    kind: AppErrorKind;
    status?: number;
    method?: string;
    path?: string;
    requestId?: string;
    safeDetails?: unknown;
    cause?: unknown;
  }) {
    super(params.message, { cause: params.cause });
    this.name = 'ApiError';
    this.kind = params.kind;
    this.status = params.status;
    this.method = params.method;
    this.path = params.path;
    this.requestId = params.requestId;
    this.safeDetails = params.safeDetails;
  }
}

const toKind = (status?: number): AppErrorKind => {
  if (status === 401) return 'auth';
  if (status === 403) return 'permission';
  if (status === 404) return 'notFound';
  if (status === 400 || status === 422) return 'validation';
  if (typeof status === 'number' && status >= 500) return 'server';
  return 'unknown';
};

const toMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return 'Request failed';
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return String(error ?? 'Unknown error');
};

export function normalizeApiError(rawError: unknown): ApiError {
  if (rawError instanceof ApiError) {
    return rawError;
  }

  if (isAxiosError(rawError)) {
    const status = rawError.response?.status;
    const method = rawError.config?.method?.toUpperCase();
    const path = rawError.config?.url;
    const requestId =
      (rawError.response?.headers?.['x-request-id'] as string | undefined) ??
      (rawError.response?.headers?.['x-correlation-id'] as string | undefined);

    if (rawError.code === 'ERR_CANCELED') {
      return new ApiError({
        message: 'Request was canceled',
        kind: 'abort',
        status,
        method,
        path,
        requestId,
        cause: rawError,
      });
    }

    if (!rawError.response) {
      return new ApiError({
        message: toMessage(rawError),
        kind: 'network',
        method,
        path,
        cause: rawError,
      });
    }

    return new ApiError({
      message: toMessage(rawError),
      kind: toKind(status),
      status,
      method,
      path,
      requestId,
      safeDetails:
        typeof rawError.response.data === 'string'
          ? { bodySnippet: rawError.response.data.slice(0, 250) }
          : undefined,
      cause: rawError,
    });
  }

  if (rawError instanceof DOMException && rawError.name === 'AbortError') {
    return new ApiError({
      message: 'Request was aborted',
      kind: 'abort',
      cause: rawError,
    });
  }

  return new ApiError({
    message: toMessage(rawError),
    kind: 'unknown',
    cause: rawError,
  });
}

export const isAbortError = (error: unknown) =>
  normalizeApiError(error).kind === 'abort';

export const getErrorMessage = (error: unknown) => normalizeApiError(error).message;

export const shouldRetryError = (error: unknown, failureCount: number) => {
  const normalized = normalizeApiError(error);
  if (normalized.kind === 'abort') return false;
  if (normalized.kind === 'auth') return false;
  if (normalized.kind === 'permission') return false;
  if (normalized.kind === 'validation') return false;
  if (normalized.kind === 'notFound') return false;
  if (normalized.kind === 'server' || normalized.kind === 'network') {
    return failureCount < 2;
  }
  return failureCount < 1;
};
